import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import type { GuideScope } from './types'

/**
 * Works out which AdvicePrep screen we are on from the URL.
 *
 * The assistant is mounted once above the router outlet rather than once per
 * page, so it cannot take orgId/callId as props — it reads them from the path
 * instead. Routes are matched most-specific-first, since
 * `/adviceprep/org/:orgId/report/:callId` and `/adviceprep/org/:id/calls` both
 * sit under the same prefix.
 *
 * Keep in step with advisoryRouteDescriptors in ../routing.tsx.
 */

export type AdvicePrepLocation = {
  scope: GuideScope
  orgId: string
  callId: string
}

const ROUTES: Array<{ pattern: string; scope: GuideScope }> = [
  { pattern: '/adviceprep/org/:orgId/report/:callId', scope: 'prep' },
  { pattern: '/adviceprep/org/:orgId/calls', scope: 'calls' },
  // A consolidated analysis IS an advisory call spanning several orgs, so its
  // groupId is the callId the deep-dive chat wants.
  { pattern: '/adviceprep/consolidated/:callId', scope: 'consolidated' },
  { pattern: '/adviceprep/consolidated', scope: 'consolidated' },
  { pattern: '/adviceprep/settings', scope: 'settings' },
  { pattern: '/adviceprep', scope: 'org-list' },
]

/** Returns null anywhere outside AdvicePrep, which is how the host knows to stay out of the way. */
export function useAdvicePrepScope(): AdvicePrepLocation | null {
  const { pathname } = useLocation()

  return useMemo(() => {
    for (const route of ROUTES) {
      const match = matchPath({ path: route.pattern, end: true }, pathname)
      if (!match) continue
      return {
        scope: route.scope,
        orgId: match.params['orgId'] ?? '',
        callId: match.params['callId'] ?? '',
      }
    }
    return null
  }, [pathname])
}
